window.theme = window.theme || {};

theme.utils = Object.assign(theme.utils || {}, {
  debounce: (fn, wait = 300) => {
    let timeoutId = null;

    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn(...args), wait);
    };
  },
  fetchConfig: (type = 'json') => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  }),
  domParser: new DOMParser(),

  togglePanel(trigger, panel) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      this.closePanel(trigger, panel);
    } else {
      this.openPanel(trigger, panel);
    }
    return !isExpanded;
  },

  openPanel(trigger, panel) {
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (panel) panel.setAttribute('aria-hidden', 'false');
  },

  closePanel(trigger, panel) {
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel) panel.setAttribute('aria-hidden', 'true');
  }
});

(() => {
  const SELECTOR = 'dialog[scroll-lock][open], details[scroll-lock][open], theme-drawer[data-open], theme-modal[data-open]';
  const hasSupport = typeof document.documentElement.matches === 'function';

  let locked = false;
  let savedY = 0;

  const isActive = () => {
    if (hasSupport && CSS.supports('selector(:has(*))')) {
      return document.documentElement.matches(`:has(${SELECTOR})`);
    }
    return !!document.querySelector(SELECTOR);
  };

  const setOverlayViewportWidth = () => {
    document.documentElement.style.setProperty('--overlay-viewport-width', `${window.innerWidth}px`);
  };

  const clearOverlayViewportWidth = () => {
    document.documentElement.style.removeProperty('--overlay-viewport-width');
  };

  const lock = () => {
    savedY = window.scrollY;
    Object.assign(document.body.style, {
      position: 'fixed',
      top: `-${savedY}px`,
      left: '0',
      right: '0',
      width: '100%',
    });
    locked = true;
  };

  const unlock = () => {
    Object.assign(document.body.style, {
      position: '',
      top: '',
      left: '',
      right: '',
      width: '',
    });
    window.scrollTo(0, savedY);
    locked = false;
  };

  const evaluate = () => {
    const mobile = window.matchMedia('(max-width: 1023px)').matches;
    const active = isActive();

    active ? setOverlayViewportWidth() : clearOverlayViewportWidth();

    if (active && !locked && mobile) {
      lock();
    } else if (!active && locked) {
      unlock();
    }
  };

  new MutationObserver(evaluate).observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['open', 'data-open'],
  });

  window.addEventListener(
    'resize',
    theme.utils.debounce(() => {
      if (isActive()) setOverlayViewportWidth();
    }, 150)
  );
})();

theme.pubsub = (() => {
  const subscribers = new Map();

  return {
    events: Object.freeze({
      CART_REQUEST_START: 'cart:request-start',
      CART_REQUEST_END: 'cart:request-end',
      CART_UPDATE: 'cart-update',
      CART_ADD: 'cart:add',
      CART_REMOVE: 'cart:remove',
      CART_CLEAR: 'cart:clear',
      CART_DRAWER_OPEN: 'cart-drawer:open',
      CART_DRAWER_CLOSE: 'cart-drawer:close',
      QUANTITY_UPDATE: 'quantity-update',
      VARIANT_CHANGE: 'variant-change',
      OPTION_VALUE_CHANGE: 'option-value-selection-change',
      CART_ERROR: 'cart-error',
      CART_ITEM_ERROR: 'cart:item-error',
      CART_FOOTER_ERROR: 'cart:footer-error',
      SEARCH_QUERY: 'search:query',
      SEARCH_RESULTS: 'search:results',
      FILTER_APPLIED: 'filter:applied',
      FILTER_CLEARED: 'filter:cleared',
      SORT_CHANGED: 'sort:changed',
      DRAWER_OPEN: 'drawer:open',
      DRAWER_CLOSE: 'drawer:close',
      MODAL_OPEN: 'modal:open',
      MODAL_CLOSE: 'modal:close',
      PAGE_TRANSITION: 'page:transition',
      FORM_SUBMIT: 'form:submit',
      FORM_ERROR: 'form:error',
      FORM_SUCCESS: 'form:success',
      SLIDER_CHANGE: 'slider:change',
      SLIDER_AUTOROTATE_TOGGLE: 'slider:autorotate-toggle',
      TOAST_SHOW: 'toast:show',
      TOAST_DISMISS: 'toast:dismiss',
      CONSENT_UPDATED: 'consent:updated',
      PAGE_RESTORED: 'page:restored',
    }),

    subscribe(eventName, callback) {
      if (!subscribers.has(eventName)) {
        subscribers.set(eventName, new Set());
      }
      subscribers.get(eventName).add(callback);

      return () => subscribers.get(eventName)?.delete(callback);
    },

    publish(eventName, data) {
      subscribers.get(eventName)?.forEach((cb) => cb(data));
    },

    clear(eventName) {
      eventName ? subscribers.delete(eventName) : subscribers.clear();
    },
  };
})();

theme.cache = (() => {
  const PREFIX = 'loomify:';
  const memory = new Map();

  const storage = (() => {
    try {
      const k = `${PREFIX}__test`;
      sessionStorage.setItem(k, '1');
      sessionStorage.removeItem(k);
      return sessionStorage;
    } catch {
      return null;
    }
  })();

  function remove(key) {
    const k = PREFIX + key;
    try { storage?.removeItem(k); } catch {}
    memory.delete(k);
  }

  function get(key) {
    const k = PREFIX + key;
    let raw = null;
    try { raw = storage ? storage.getItem(k) : null; } catch {}
    if (raw === null) raw = memory.get(k) ?? null;
    if (!raw) return null;
    try {
      const { v, e } = JSON.parse(raw);
      if (e && e < Date.now()) { remove(key); return null; }
      return v;
    } catch {
      remove(key);
      return null;
    }
  }

  function set(key, value, ttlMs) {
    const k = PREFIX + key;
    let payload;
    try {
      payload = JSON.stringify({ v: value, e: ttlMs ? Date.now() + ttlMs : 0 });
    } catch {
      return;
    }
    try {
      if (storage) storage.setItem(k, payload);
      else memory.set(k, payload);
    } catch {
      memory.set(k, payload);
    }
  }

  function clear(namespace) {
    const scope = PREFIX + (namespace || '');
    try {
      if (storage) {
        for (let i = storage.length - 1; i >= 0; i--) {
          const key = storage.key(i);
          if (key && key.startsWith(scope)) storage.removeItem(key);
        }
      }
    } catch {}
    for (const key of memory.keys()) {
      if (key.startsWith(scope)) memory.delete(key);
    }
  }

  const TTL_SHORT = 5 * 60 * 1000;
  const TTL_FRESH = 60 * 1000;
  const MAX_CACHEABLE_LENGTH = 250000;
  const EPOCH_KEY = 'volatile-epoch';
  const inflight = new Map();

  const scope = () => {
    const shopify = window.Shopify || {};
    return [shopify.locale, shopify.country, shopify.currency?.active].join('|');
  };

  const cache = {
    get,
    set,
    delete: remove,
    clear,
    TTL_SHORT,
    TTL_FRESH,
    MAX_CACHEABLE_LENGTH,

    scopedKey(key) {
      return `${key}|${scope()}`;
    },

    invalidateVolatile() {
      remove(EPOCH_KEY);
      set(EPOCH_KEY, Date.now(), 0);
      inflight.clear();
    },

    request(url, { ttl = TTL_FRESH, read = (response) => response.text(), init } = {}) {
      const key = cache.scopedKey(String(url));
      const epoch = get(EPOCH_KEY) || 0;
      const hit = get(key);

      if (hit && hit.at > epoch) return Promise.resolve(hit.value);
      if (inflight.has(key)) return inflight.get(key);

      const startedAt = Date.now();
      const promise = fetch(url, init)
        .then((response) => {
          if (!response.ok) throw new Error(`Request failed: ${response.status}`);
          return read(response);
        })
        .then((value) => {
          const tooLarge = typeof value === 'string' && value.length > MAX_CACHEABLE_LENGTH;
          if (!tooLarge && startedAt > (get(EPOCH_KEY) || 0)) set(key, { at: startedAt, value }, ttl);
          return value;
        })
        .finally(() => {
          if (inflight.get(key) === promise) inflight.delete(key);
        });

      inflight.set(key, promise);
      return promise;
    },
  };

  return cache;
})();

[theme.pubsub.events.CART_UPDATE, theme.pubsub.events.CART_ADD].forEach((eventName) => {
  theme.pubsub.subscribe(eventName, () => theme.cache.invalidateVolatile());
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    theme.pubsub.publish(theme.pubsub.events.PAGE_RESTORED, {});
  }
});

